<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  activeStates: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['format']);

const { t } = useI18n();

const tooltip = ref(null);

const handleCommand = button => {
  emit('format', { button });
};

const setAlignment = align => {
  emit('format', { button: 'align', value: align });
};

const showTooltip = label => {
  tooltip.value = label;
};

const hideTooltip = () => {
  tooltip.value = null;
};

const isActive = button => props.activeStates[button] || false;
</script>

<template>
  <div
    class="bg-white dark:bg-[#1D1D1F] rounded-lg shadow-sm border border-[#E5E7EB] dark:border-[#2A2A2C] flex items-center gap-1 p-1 w-fit"
  >
    <!-- Text Formatting Section -->
    <div class="relative">
      <button
        class="h-8 w-8 flex items-center justify-center rounded-md transition-colors duration-200 hover:bg-[#3860F9]/10 dark:hover:bg-[#D3FD58]/10 focus:outline-none"
        :class="{
          'bg-[#3860F9]/10 dark:bg-[#D3FD58]/10 text-[#3860F9] dark:text-[#D3FD58]':
            isActive('bold'),
        }"
        :aria-label="t('TASKS.TOOLBAR.BOLD')"
        @click="handleCommand('bold')"
        @mouseenter="showTooltip('Bold')"
        @mouseleave="hideTooltip"
      >
        <span class="i-lucide-bold size-4" />
      </button>
      <div
        v-if="tooltip === 'Bold'"
        class="absolute bottom-10 left-1/2 -translate-x-1/2 bg-[#1D1D1F] dark:bg-white text-white dark:text-[#1D1D1F] text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap font-medium z-50 animate-fade-in-up"
      >
        {{ t('TASKS.TOOLBAR.BOLD') }}
      </div>
    </div>

    <div class="relative">
      <button
        class="h-8 w-8 flex items-center justify-center rounded-md transition-colors duration-200 hover:bg-[#3860F9]/10 dark:hover:bg-[#D3FD58]/10 focus:outline-none"
        :class="{
          'bg-[#3860F9]/10 dark:bg-[#D3FD58]/10 text-[#3860F9] dark:text-[#D3FD58]':
            isActive('italic'),
        }"
        :aria-label="t('TASKS.TOOLBAR.ITALIC')"
        @click="handleCommand('italic')"
        @mouseenter="showTooltip('Italic')"
        @mouseleave="hideTooltip"
      >
        <span class="i-lucide-italic size-4" />
      </button>
      <div
        v-if="tooltip === 'Italic'"
        class="absolute bottom-10 left-1/2 -translate-x-1/2 bg-[#1D1D1F] dark:bg-white text-white dark:text-[#1D1D1F] text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap font-medium z-50 animate-fade-in-up"
      >
        {{ t('TASKS.TOOLBAR.ITALIC') }}
      </div>
    </div>

    <div class="relative">
      <button
        class="h-8 w-8 flex items-center justify-center rounded-md transition-colors duration-200 hover:bg-[#3860F9]/10 dark:hover:bg-[#D3FD58]/10 focus:outline-none"
        :class="{
          'bg-[#3860F9]/10 dark:bg-[#D3FD58]/10 text-[#3860F9] dark:text-[#D3FD58]':
            isActive('underline'),
        }"
        :aria-label="t('TASKS.TOOLBAR.UNDERLINE')"
        @click="handleCommand('underline')"
        @mouseenter="showTooltip('Underline')"
        @mouseleave="hideTooltip"
      >
        <span class="i-lucide-underline size-4" />
      </button>
      <div
        v-if="tooltip === 'Underline'"
        class="absolute bottom-10 left-1/2 -translate-x-1/2 bg-[#1D1D1F] dark:bg-white text-white dark:text-[#1D1D1F] text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap font-medium z-50 animate-fade-in-up"
      >
        {{ t('TASKS.TOOLBAR.UNDERLINE') }}
      </div>
    </div>

    <div class="relative">
      <button
        class="h-8 w-8 flex items-center justify-center rounded-md transition-colors duration-200 hover:bg-[#3860F9]/10 dark:hover:bg-[#D3FD58]/10 focus:outline-none"
        :class="{
          'bg-[#3860F9]/10 dark:bg-[#D3FD58]/10 text-[#3860F9] dark:text-[#D3FD58]':
            isActive('strikethrough'),
        }"
        :aria-label="t('TASKS.TOOLBAR.STRIKETHROUGH')"
        @click="handleCommand('strikethrough')"
        @mouseenter="showTooltip('Strikethrough')"
        @mouseleave="hideTooltip"
      >
        <span class="i-lucide-strikethrough size-4" />
      </button>
      <div
        v-if="tooltip === 'Strikethrough'"
        class="absolute bottom-10 left-1/2 -translate-x-1/2 bg-[#1D1D1F] dark:bg-white text-white dark:text-[#1D1D1F] text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap font-medium z-50 animate-fade-in-up"
      >
        {{ t('TASKS.TOOLBAR.STRIKETHROUGH') }}
      </div>
    </div>

    <div class="relative">
      <button
        class="h-8 w-8 flex items-center justify-center rounded-md transition-colors duration-200 hover:bg-n-brand/10 focus:outline-none"
        :class="{ 'bg-n-brand/10 text-n-brand': isActive('link') }"
        :aria-label="t('TASKS.TOOLBAR.LINK')"
        @click="handleCommand('link')"
        @mouseenter="showTooltip('Link')"
        @mouseleave="hideTooltip"
      >
        <span class="i-lucide-link size-4" />
      </button>
      <div
        v-if="tooltip === 'Link'"
        class="absolute bottom-10 left-1/2 -translate-x-1/2 bg-[#1D1D1F] dark:bg-white text-white dark:text-[#1D1D1F] text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap font-medium z-50 animate-fade-in-up"
      >
        {{ t('TASKS.TOOLBAR.LINK') }}
      </div>
    </div>

    <div class="relative">
      <button
        class="h-8 w-8 flex items-center justify-center rounded-md transition-colors duration-200 hover:bg-n-brand/10 focus:outline-none"
        :class="{ 'bg-n-brand/10 text-n-brand': isActive('heading') }"
        :aria-label="t('TASKS.TOOLBAR.HEADING')"
        @click="handleCommand('heading')"
        @mouseenter="showTooltip('Heading')"
        @mouseleave="hideTooltip"
      >
        <span class="i-lucide-heading size-4" />
      </button>
      <div
        v-if="tooltip === 'Heading'"
        class="absolute bottom-10 left-1/2 -translate-x-1/2 bg-[#1D1D1F] dark:bg-white text-white dark:text-[#1D1D1F] text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap font-medium z-50 animate-fade-in-up"
      >
        {{ t('TASKS.TOOLBAR.HEADING') }}
      </div>
    </div>

    <div class="relative">
      <button
        class="h-8 w-8 flex items-center justify-center rounded-md transition-colors duration-200 hover:bg-n-brand/10 focus:outline-none"
        :class="{ 'bg-n-brand/10 text-n-brand': isActive('quote') }"
        :aria-label="t('TASKS.TOOLBAR.QUOTE')"
        @click="handleCommand('quote')"
        @mouseenter="showTooltip('Quote')"
        @mouseleave="hideTooltip"
      >
        <span class="i-lucide-quote size-4" />
      </button>
      <div
        v-if="tooltip === 'Quote'"
        class="absolute bottom-10 left-1/2 -translate-x-1/2 bg-[#1D1D1F] dark:bg-white text-white dark:text-[#1D1D1F] text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap font-medium z-50 animate-fade-in-up"
      >
        {{ t('TASKS.TOOLBAR.QUOTE') }}
      </div>
    </div>

    <!-- Divider -->
    <div class="w-px h-8 bg-n-weak" />

    <!-- Highlight and Color Section -->
    <div class="relative">
      <button
        class="h-8 w-8 flex items-center justify-center rounded-md transition-colors duration-200 hover:bg-n-brand/10 focus:outline-none"
        :class="{ 'bg-n-brand/10 text-n-brand': isActive('highlight') }"
        :aria-label="t('TASKS.TOOLBAR.HIGHLIGHT')"
        @click="handleCommand('highlight')"
        @mouseenter="showTooltip('Highlight')"
        @mouseleave="hideTooltip"
      >
        <span class="i-lucide-highlighter size-4" />
      </button>
      <div
        v-if="tooltip === 'Highlight'"
        class="absolute bottom-10 left-1/2 -translate-x-1/2 bg-[#1D1D1F] dark:bg-white text-white dark:text-[#1D1D1F] text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap font-medium z-50 animate-fade-in-up"
      >
        {{ t('TASKS.TOOLBAR.HIGHLIGHT') }}
      </div>
    </div>

    <div class="relative">
      <button
        class="h-8 w-8 flex items-center justify-center rounded-md transition-colors duration-200 hover:bg-n-brand/10 focus:outline-none"
        :class="{ 'bg-n-brand/10 text-n-brand': isActive('color') }"
        :aria-label="t('TASKS.TOOLBAR.COLOR')"
        @click="handleCommand('color')"
        @mouseenter="showTooltip('Color')"
        @mouseleave="hideTooltip"
      >
        <span class="i-lucide-palette size-4" />
      </button>
      <div
        v-if="tooltip === 'Color'"
        class="absolute bottom-10 left-1/2 -translate-x-1/2 bg-[#1D1D1F] dark:bg-white text-white dark:text-[#1D1D1F] text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap font-medium z-50 animate-fade-in-up"
      >
        {{ t('TASKS.TOOLBAR.COLOR') }}
      </div>
    </div>

    <!-- Divider -->
    <div class="w-px h-8 bg-n-weak" />

    <!-- Text Alignment Section -->
    <div class="relative">
      <button
        class="h-8 w-8 flex items-center justify-center rounded-md transition-colors duration-200 hover:bg-n-brand/10 focus:outline-none"
        :class="{ 'bg-n-brand/10 text-n-brand': isActive('alignLeft') }"
        :aria-label="t('TASKS.TOOLBAR.ALIGN_LEFT')"
        @click="setAlignment('left')"
        @mouseenter="showTooltip('Align Left')"
        @mouseleave="hideTooltip"
      >
        <span class="i-lucide-align-left size-4" />
      </button>
      <div
        v-if="tooltip === 'Align Left'"
        class="absolute bottom-10 left-1/2 -translate-x-1/2 bg-[#1D1D1F] dark:bg-white text-white dark:text-[#1D1D1F] text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap font-medium z-50 animate-fade-in-up"
      >
        {{ t('TASKS.TOOLBAR.ALIGN_LEFT') }}
      </div>
    </div>

    <div class="relative">
      <button
        class="h-8 w-8 flex items-center justify-center rounded-md transition-colors duration-200 hover:bg-n-brand/10 focus:outline-none"
        :class="{ 'bg-n-brand/10 text-n-brand': isActive('alignCenter') }"
        :aria-label="t('TASKS.TOOLBAR.ALIGN_CENTER')"
        @click="setAlignment('center')"
        @mouseenter="showTooltip('Align Center')"
        @mouseleave="hideTooltip"
      >
        <span class="i-lucide-align-center size-4" />
      </button>
      <div
        v-if="tooltip === 'Align Center'"
        class="absolute bottom-10 left-1/2 -translate-x-1/2 bg-[#1D1D1F] dark:bg-white text-white dark:text-[#1D1D1F] text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap font-medium z-50 animate-fade-in-up"
      >
        {{ t('TASKS.TOOLBAR.ALIGN_CENTER') }}
      </div>
    </div>

    <div class="relative">
      <button
        class="h-8 w-8 flex items-center justify-center rounded-md transition-colors duration-200 hover:bg-n-brand/10 focus:outline-none"
        :class="{ 'bg-n-brand/10 text-n-brand': isActive('alignRight') }"
        :aria-label="t('TASKS.TOOLBAR.ALIGN_RIGHT')"
        @click="setAlignment('right')"
        @mouseenter="showTooltip('Align Right')"
        @mouseleave="hideTooltip"
      >
        <span class="i-lucide-align-right size-4" />
      </button>
      <div
        v-if="tooltip === 'Align Right'"
        class="absolute bottom-10 left-1/2 -translate-x-1/2 bg-[#1D1D1F] dark:bg-white text-white dark:text-[#1D1D1F] text-xs rounded-md px-2 py-1 shadow-lg whitespace-nowrap font-medium z-50 animate-fade-in-up"
      >
        {{ t('TASKS.TOOLBAR.ALIGN_RIGHT') }}
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translate(-50%, 4px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

.animate-fade-in-up {
  animation: fade-in-up 200ms ease-out;
}
</style>
